import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Building2, Mail, Phone, MapPin, Globe, Save, Upload, Palette } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

export const SettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tenant, setTenant] = useState(null);
  
  const [generalForm, setGeneralForm] = useState({
    name: '',
    slug: '',
    description: '',
  });
  
  const [contactForm, setContactForm] = useState({
    contact_email: '',
    contact_phone: '',
    address: '',
    website: '',
  });
  
  const [brandingForm, setBrandingForm] = useState({
    logo_url: '',
    primary_color: '#1e40af',
    secondary_color: '#f59e0b',
  });

  useEffect(() => {
    fetchTenantSettings();
  }, []);

  const fetchTenantSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tenant/settings`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setTenant(data);
        
        setGeneralForm({
          name: data.name || '',
          slug: data.slug || '',
          description: data.description || '',
        });
        
        setContactForm({
          contact_email: data.contact_email || '',
          contact_phone: data.contact_phone || '',
          address: data.address || '',
          website: data.website || '',
        });
        
        setBrandingForm({
          logo_url: data.logo_url || '',
          primary_color: data.settings?.primary_color || '#1e40af',
          secondary_color: data.settings?.secondary_color || '#f59e0b',
        });
      }
    } catch (error) {
      console.error('Failed to fetch tenant settings:', error);
      toast.error('Ayarlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGeneral = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/tenant/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify(generalForm),
      });
      
      if (response.ok) {
        toast.success('Genel ayarlar kaydedildi');
        fetchTenantSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Kaydetme başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveContact = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/tenant/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify(contactForm),
      });
      
      if (response.ok) {
        toast.success('İletişim bilgileri kaydedildi');
        fetchTenantSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Kaydetme başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBranding = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/tenant/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          logo_url: brandingForm.logo_url,
          settings: {
            primary_color: brandingForm.primary_color,
            secondary_color: brandingForm.secondary_color,
          }
        }),
      });
      
      if (response.ok) {
        toast.success('Marka ayarları kaydedildi');
        fetchTenantSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Kaydetme başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 bg-primary/10 rounded-lg">
          <Building2 className="h-8 w-8 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Kurum Ayarları</h1>
          <p className="text-slate-500">Kurumunuzun bilgilerini ve görünümünü özelleştirin</p>
        </div>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList>
          <TabsTrigger value="general" data-testid="general-tab">Genel</TabsTrigger>
          <TabsTrigger value="contact" data-testid="contact-tab">İletişim</TabsTrigger>
          <TabsTrigger value="branding" data-testid="branding-tab">Marka</TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>Genel Bilgiler</CardTitle>
              <CardDescription>Kurumunuzun temel bilgilerini düzenleyin</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Kurum Adı</Label>
                  <Input
                    id="name"
                    value={generalForm.name}
                    onChange={(e) => setGeneralForm({ ...generalForm, name: e.target.value })}
                    placeholder="Örn: BilgeOkur İlkokulu"
                    data-testid="tenant-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">URL Kodu</Label>
                  <Input
                    id="slug"
                    value={generalForm.slug}
                    onChange={(e) => setGeneralForm({ ...generalForm, slug: e.target.value })}
                    placeholder="Örn: bilgeokur-ilkokulu"
                    data-testid="tenant-slug-input"
                  />
                  <p className="text-xs text-slate-500">URL'de kullanılacak benzersiz kod</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Açıklama</Label>
                <Textarea
                  id="description"
                  value={generalForm.description}
                  onChange={(e) => setGeneralForm({ ...generalForm, description: e.target.value })}
                  placeholder="Kurumunuz hakkında kısa bir açıklama..."
                  rows={3}
                  data-testid="tenant-description-input"
                />
              </div>
              <div className="flex justify-end">
                <Button onClick={handleSaveGeneral} disabled={saving} data-testid="save-general-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Settings */}
        <TabsContent value="contact">
          <Card>
            <CardHeader>
              <CardTitle>İletişim Bilgileri</CardTitle>
              <CardDescription>Kurumunuzun iletişim bilgilerini düzenleyin</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" /> E-posta
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={contactForm.contact_email}
                    onChange={(e) => setContactForm({ ...contactForm, contact_email: e.target.value })}
                    placeholder="iletisim@kurum.com"
                    data-testid="tenant-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" /> Telefon
                  </Label>
                  <Input
                    id="phone"
                    value={contactForm.contact_phone}
                    onChange={(e) => setContactForm({ ...contactForm, contact_phone: e.target.value })}
                    placeholder="0312 123 45 67"
                    data-testid="tenant-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="website" className="flex items-center gap-2">
                  <Globe className="h-4 w-4" /> Web Sitesi
                </Label>
                <Input
                  id="website"
                  value={contactForm.website}
                  onChange={(e) => setContactForm({ ...contactForm, website: e.target.value })}
                  placeholder="https://www.kurum.com"
                  data-testid="tenant-website-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Adres
                </Label>
                <Textarea
                  id="address"
                  value={contactForm.address}
                  onChange={(e) => setContactForm({ ...contactForm, address: e.target.value })}
                  placeholder="Tam adres..."
                  rows={2}
                  data-testid="tenant-address-input"
                />
              </div>
              <div className="flex justify-end">
                <Button onClick={handleSaveContact} disabled={saving} data-testid="save-contact-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Settings */}
        <TabsContent value="branding">
          <Card>
            <CardHeader>
              <CardTitle>Marka Ayarları</CardTitle>
              <CardDescription>Kurumunuzun görsel kimliğini özelleştirin</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <Label>Kurum Logosu</Label>
                <div className="flex items-center gap-4">
                  {brandingForm.logo_url ? (
                    <img 
                      src={brandingForm.logo_url} 
                      alt="Logo" 
                      className="h-16 w-auto object-contain border rounded-lg p-2"
                    />
                  ) : (
                    <div className="h-16 w-32 border-2 border-dashed rounded-lg flex items-center justify-center text-slate-400">
                      Logo Yok
                    </div>
                  )}
                  <div className="space-y-2">
                    <Input
                      value={brandingForm.logo_url}
                      onChange={(e) => setBrandingForm({ ...brandingForm, logo_url: e.target.value })}
                      placeholder="Logo URL'si girin"
                      data-testid="tenant-logo-input"
                    />
                    <p className="text-xs text-slate-500">PNG veya SVG formatında, şeffaf arkaplan önerilir</p>
                  </div>
                </div>
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="primaryColor" className="flex items-center gap-2">
                    <Palette className="h-4 w-4" /> Ana Renk
                  </Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      id="primaryColor"
                      value={brandingForm.primary_color}
                      onChange={(e) => setBrandingForm({ ...brandingForm, primary_color: e.target.value })}
                      className="h-10 w-14 rounded cursor-pointer"
                    />
                    <Input
                      value={brandingForm.primary_color}
                      onChange={(e) => setBrandingForm({ ...brandingForm, primary_color: e.target.value })}
                      placeholder="#1e40af"
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondaryColor" className="flex items-center gap-2">
                    <Palette className="h-4 w-4" /> İkincil Renk
                  </Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      id="secondaryColor"
                      value={brandingForm.secondary_color}
                      onChange={(e) => setBrandingForm({ ...brandingForm, secondary_color: e.target.value })}
                      className="h-10 w-14 rounded cursor-pointer"
                    />
                    <Input
                      value={brandingForm.secondary_color}
                      onChange={(e) => setBrandingForm({ ...brandingForm, secondary_color: e.target.value })}
                      placeholder="#f59e0b"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSaveBranding} disabled={saving} data-testid="save-branding-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
